import { z } from 'zod';

import {
  filterParamKeys,
  indexSearchSchema,
  listParams,
  type ListParams,
  type SearchPatch,
} from '@nucleus-commerce/admin-kit/index-table';
import { ExportKind, ImportKind } from '@nucleus-commerce/api-client/admin';

import { JOB_FILTER_FIELDS, JOB_SORTS } from '../components/job-filters';

/** Every import kind is an export kind; not every export kind can be imported (orders, newsletter, …). */
export function isImportKind(kind: string): kind is ImportKind {
  return (Object.values(ImportKind) as string[]).includes(kind);
}

export const JOB_TABS = ['exports', 'imports'] as const;
export type JobTab = (typeof JOB_TABS)[number];

/**
 * URL search of the imports and exports page: the visible tab's job table (`kind`, `status`,
 * `createdAt_gte` / `_lte`, `sort`, `cursor`, `limit`), plus `new`, the kind the form starts on
 * (set by the Import and Export buttons on the list pages). The export fields take every kind;
 * each tab's filters then keep only their own. Neither endpoint has a text search, so `q` is
 * dropped rather than kept and sent.
 */
export const importsExportsSearchSchema = indexSearchSchema(JOB_FILTER_FIELDS.exports, {
  sort: JOB_SORTS,
}).extend({
  tab: z.enum(JOB_TABS).optional().catch(undefined),
  new: z.enum(ExportKind).optional().catch(undefined),
  q: z.undefined().catch(undefined),
});

export type ImportsExportsSearch = z.infer<typeof importsExportsSearchSchema>;

/** The list request of one tab for a validated search; that tab's list params match it. */
export function jobListParams(search: ImportsExportsSearch, tab: JobTab): ListParams {
  return listParams(search, JOB_FILTER_FIELDS[tab]);
}

/** Clears every list param, so switching tabs starts the other list unfiltered. */
export const CLEAR_JOB_LIST: SearchPatch = Object.fromEntries(
  [...JOB_FILTER_FIELDS.exports.flatMap((field) => filterParamKeys(field)), 'sort', 'cursor'].map(
    (key) => [key, undefined],
  ),
);
